import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface PriceAlert {
  id: string;
  user_id: string;
  saved_item_id: string | null;
  product_name: string;
  product_url: string | null;
  product_image: string | null;
  current_price: number;
  target_price: number;
  currency: string;
  is_active: boolean;
  is_triggered: boolean;
  triggered_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePriceAlertInput {
  saved_item_id?: string;
  product_name: string;
  product_url?: string;
  product_image?: string;
  current_price: number;
  target_price: number;
  currency?: string;
}

export function usePriceAlerts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: alerts = [], isLoading, error } = useQuery({
    queryKey: ['price-alerts', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('price_alerts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as PriceAlert[];
    },
    enabled: !!user,
  });

  const createAlert = useMutation({
    mutationFn: async (input: CreatePriceAlertInput) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('price_alerts')
        .insert({
          ...input,
          user_id: user.id,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PriceAlert;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts', user?.id] });
    },
  });

  const updateAlert = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PriceAlert> & { id: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('price_alerts')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PriceAlert;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts', user?.id] });
    },
  });

  const deleteAlert = useMutation({
    mutationFn: async (alertId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('price_alerts')
        .delete()
        .eq('id', alertId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts', user?.id] });
    },
  });

  // Check if alert exists for a product
  const getAlertForProduct = (productName: string) => {
    return alerts.find(a => a.product_name === productName && a.is_active);
  };

  // Check if there's a triggered alert for a saved item
  const getTriggeredAlertForItem = (savedItemId: string) => {
    return alerts.find(a => a.saved_item_id === savedItemId && a.is_triggered);
  };

  // Check if there's any active alert for a saved item
  const getAlertForItem = (savedItemId: string) => {
    return alerts.find(a => a.saved_item_id === savedItemId && a.is_active);
  };

  return {
    alerts,
    isLoading,
    error,
    createAlert,
    updateAlert,
    deleteAlert,
    getAlertForProduct,
    getTriggeredAlertForItem,
    getAlertForItem,
  };
}